import React, { useEffect, useState } from "react";
import { Typography, Box } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { LoadingButton } from "@mui/lab";
import Div from "@jumbo/shared/Div";
import { uploadQcImages, getQcImages, deleteQcImage } from "app/services/apis/UploadImagesApi";
import Swal from "sweetalert2";

const QcUploadImagesModal = ({ open, onClose, rowData }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const permissions = JSON.parse(sessionStorage.getItem("permissions") || "{}");

  const fetchImages = async () => {
    if (!rowData?.qc_details_id) return;
    setFetching(true);
    try {
      const res = await getQcImages(rowData.qc_details_id);
      setImages(res || []);
      setSelectedForDelete([]);
    } catch (error) {
      console.error(error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchImages();
    }
  }, [open, rowData]);

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("image", files[i]);
    }

    setLoading(true);
    try {
      await uploadQcImages(formData, rowData.qc_details_id);
      Swal.fire("Success", "Images uploaded successfully", "success");
      fetchImages();
    } catch (error) {
      Swal.fire("Error", "Failed to upload images", "error");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const handleDeleteMultiple = async () => {
    if (selectedForDelete.length === 0) return;
    try {
      const confirm = await Swal.fire({
        title: "Are you sure?",
        text: `You want to delete the ${selectedForDelete.length} selected images?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete them!"
      });
      if (confirm.isConfirmed) {
        setLoading(true);
        await Promise.all(
          selectedForDelete.map((img) => deleteQcImage(rowData.qc_details_id, img))
        );
        Swal.fire("Deleted!", "Selected images have been deleted.", "success");
        fetchImages();
      }
    } catch (error) {
      Swal.fire("Error", "Failed to delete selected images", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <style>{`
        .swal2-container {
          z-index: 99999 !important;
        }
      `}</style>
      <DialogTitle sx={{ fontWeight: "700", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>QC Done Uploaded Images (Order No: {rowData?.order_no})</span>
        {permissions.qc_order_edit === true && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {selectedForDelete.length > 0 && (
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={handleDeleteMultiple}
                disabled={loading}
              >
                Delete Selected ({selectedForDelete.length})
              </Button>
            )}
            <input
              type="file"
              multiple
              accept=".jpg, .jpeg, .png"
              id="qc-image-upload-input"
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
            <label htmlFor="qc-image-upload-input">
              <LoadingButton
                loading={loading}
                component="span"
                size="small"
                variant="contained"
                color="primary"
              >
                Upload New Images
              </LoadingButton>
            </label>
          </Box>
        )}
      </DialogTitle>
      <DialogContent>
        {fetching ? (
          <Typography>Loading images...</Typography>
        ) : images.length > 0 ? (
          <Div
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-start",
              flexWrap: "wrap",
              mt: 2
            }}
          >
            {images.map((img, idx) => {
              const fullUrl = `${process.env.REACT_APP_URL}/${img}`;
              const isSelected = selectedForDelete.includes(img);
              return (
                <Box key={idx} sx={{ position: "relative", border: "1px solid #ddd", p: 0.5, borderRadius: 1 }}>
                  {permissions.qc_order_edit === true && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedForDelete([...selectedForDelete, img]);
                        } else {
                          setSelectedForDelete(selectedForDelete.filter((x) => x !== img));
                        }
                      }}
                      style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                        zIndex: 2
                      }}
                    />
                  )}
                  <img
                    loading="lazy"
                    height="180px"
                    width="140px"
                    style={{ cursor: "pointer", objectFit: "cover" }}
                    src={fullUrl}
                    alt="QC"
                    onClick={() => window.open(fullUrl, "_blank")}
                  />
                </Box>
              );
            })}
          </Div>
        ) : (
          <Typography variant="h3" align="center" color="textSecondary" sx={{ my: 4 }}>
            No Images Uploaded for this QC Report.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose} color="error" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QcUploadImagesModal;
